import type { MouseEvent, JSX } from 'react';
import { Button, MenuButton, IconButton, Icon, useThemeColor, useClipboard } from '@wistia/vhs';
import { trackStructEvent } from '~/utilities/snowplow';
import type { Nilable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import type { CommentPermissions } from './types';
import { useCommentContext } from './CommentContext';

export const CommentContextMenu = ({
  commentId,
  permissions,
  commentHasThread,
  setShowEditForm,
  isParentComment,
  commentTimestamp,
}: {
  commentId: string;
  permissions: CommentPermissions;
  commentHasThread: boolean;
  setShowEditForm: (show: boolean) => void;
  isParentComment: boolean;
  commentTimestamp: Nilable<number>;
}): JSX.Element | null => {
  const grey500 = useThemeColor('grey500');
  const { deleteComment, commentingView } = useCommentContext();

  const { origin, pathname } = window.location;
  const commentParam = `?comment=${encodeURIComponent(commentId)}`;
  const timestampParam = isNotNil(commentTimestamp)
    ? `&timestamp=${encodeURIComponent(commentTimestamp)}`
    : '';
  const audienceTabParam = commentingView === 'Audience' ? '&audienceTab=true' : '';
  const commentLink = `${origin}${pathname}${commentParam}${timestampParam}${audienceTabParam}`;

  const [onCopy] = useClipboard(commentLink);

  const { canEdit, canDelete } = permissions;
  const deleteText = commentHasThread ? 'Delete thread' : 'Delete';

  const menuItems: JSX.Element[] = [];

  if (canEdit) {
    menuItems.push(
      <Button
        key="edit"
        label="Edit"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          setShowEditForm(true);
        }}
      />,
    );
  }

  if (isParentComment) {
    menuItems.push(
      <Button
        key="copy-link"
        label="Copy link"
        onClick={() => {
          void onCopy();
        }}
      />,
    );
  }

  if (canDelete) {
    menuItems.push(
      <Button
        key="delete"
        label={deleteText}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          deleteComment(commentId);
          trackStructEvent({
            category: 'button',
            action: 'click',
            label: `delete-media-page-${commentingView.toLowerCase()}-comment`,
          });
        }}
        variant="dangerous"
      />,
    );
  }

  return menuItems.length > 0 ? (
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    <MenuButton
      data-testid="comment-context-menu"
      iconButtonComponent={
        <IconButton
          colorOverride={grey500}
          icon={<Icon type="ellipsis" />}
          preventTooltip={true}
        />
      }
      label="Actions"
      menuAlignment="left"
      menuItems={menuItems}
      menuPosition="below"
      onMenuClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
      }}
    />
  ) : null;
};
